import { FieldValues, UseFormReturn } from "react-hook-form";
import { v4 as uuid } from "uuid";
import { new_ErrorAlert } from "../export_spwk/ExportManager";
import { Lang, SpwkUtil } from "./SpwkUtil";
import { HisRule, SpwkUtilV3, VisibleMode } from "./SpwkUtilV3";
import { UniForm } from "./UniForm";
import { UniOption } from "./UniOption";

export class UniQuestion {
  static END_POINT = "uni_question";

  //#region  --- Columns ---
  serno = 0;
  guid; //= uuid();

  form_guid = "";

  sort_no = 1000;

  chapter = "";
  kind = "";

  variant = "";

  /** 是否為新版合併專用的(0原稿不存在、1合併用的版本)
   * - 通用Form裡面的 既往病史 與 自覺症狀 固定的題目分類
   * - 不在職安屬Word但是拆分出來的選項
   */
  v3_mode = 0;

  /** HIS章節代碼(類別) */
  ch_his_code = "";

  /** HIS解碼規則 */
  q_his_rule: HisRule = HisRule.None;

  /** HIS題目代碼(項目) */
  q_his_code = "";

  /** 申報題目代碼 */
  q_gov_code = "";

  question_code = "";
  question_title = "";

  /** 備註(僅給人看，系統不使用此欄位) */
  remark = "";

  /** 標題(英語) */
  title_eng = "";

  /** 標題(印尼) */
  title_ind = "";

  /** 標題(泰國) */
  title_tha = "";

  /** 標題(菲律賓) */
  title_tgl = "";

  /** 標題(越南) */
  title_vie = "";

  //#region 介面排版相關屬性

  /** 手機: 標題是否維持一行 */
  xs_inline = "";

  /** 平板: 標題是否維持一行 */
  sm_inline = "";

  /** 手機: 選項預設寬度 */
  xs_opt_w = "";

  /** 平板: 選項預設寬度 */
  sm_opt_w = "";

  /** 手機: 題目下方留白 */
  xs_pb = "";

  /** 平板: 題目下方留白 */
  sm_pb = "";

  is_a01 = 0;
  is_a98 = 0;
  is_b01 = 0;
  is_b02 = 0;

  question_filter = "";

  q_visible: VisibleMode = VisibleMode.皆可見;

  /** 哪一題，數值是什麼的時候，才顯示或是Enabled  
  詳見: ValidWhenRule 註解
  */
  valid_when_rule: ValidWhenRule = ValidWhenRule.None;
  valid_when_rhf_name: string = ""; // rhf.watch 要看的選項
  valid_when_rhf_value: string = ""; // rhf.watch 回傳的值等於 valid_when_rhf_value 就隱藏 Question
  is_required = "1";

  HasFunc2(spwk_list: string[]): boolean {
    let filter = this.question_filter || "";
    if (filter === "") {
      return true;
    }

    const pattern = /(?<exclude>!)?(?<code>[^﹋]+)/g;
    const matches = Array.from(filter.matchAll(pattern));

    // 排除重複項
    const allSpwk = new Set(spwk_list);

    // 僅限特作
    const restrictList = new Set(
      matches
        .filter((match) => (match.groups?.exclude ? false : true))
        .map((match) => match.groups?.code)
    );

    // 排除特作
    const excludeList = new Set(
      matches
        .filter((match) => (match.groups?.exclude ? true : false))
        .map((match) => match.groups?.code)
    );

    return (
      (restrictList.size > 0 &&
        Array.from(allSpwk).some((code) => restrictList.has(code))) ||
      (excludeList.size > 0 &&
        Array.from(allSpwk).some((code) => !excludeList.has(code)))
    );
  }

  //#endregion

  //#endregion

  //#region --- View ---
  func_guid: string | null = null;
  //#endregion

  get_Question_RhfName(): string {
    if (SpwkUtilV3.IsEnabled) {
      return SpwkUtilV3.get_Question_RhfName_V3(
        this.chapter,
        this.question_code
      );
    } else {
      return SpwkUtil.get_Question_RhfName(this.chapter, this.question_code);
    }
  }

  constructor() {
    this.guid = uuid();
  }

  //#region --- 手動填入 ---
  OwnerForm?: UniForm;
  Options?: UniOption[];

  //#endregion

  /** 取得主要語言 */
  getTitleLangA(lang: Lang) {
    return SpwkUtil.__getQuestionTitleByLang(this, lang);
  }

  /** 取得次要語言 */
  getTitleLangB(lang: Lang) {
    return SpwkUtil.__getQuestionTitleByLang(this, lang);
  }
}

/**
* 時間關係，先寫死幾個規則，之後再改為通解

* A. 自覺症狀.題目 IF(檢查時期=1新進、3變更 填 題目1)
* B. 自覺症狀.題目 IF(檢查時期=2定期、4追蹤 填 題目2~4)
* C. B02.作業經歷.5.2 -> (3)若有，為何種類型？(多選題) □耳罩 □耳塞 □其他 
* C. B02.作業經歷.5.2 -> (4)工作佩戴時間？□全時間佩戴 □一半時間佩戴  □都不戴
* D. B02.既往病史.3. 上述問題若有，請填寫
*/
export enum ValidWhenRule {
  None = "",
  是否曾經從事其他工作 = "是否曾經從事其他工作",
  B02_新進_變更 = "B02_新進_變更", // A
  B02_定期_追蹤 = "B02_定期_追蹤", // B
  B02_是否配戴護具 = "B02_是否配戴護具", // C
  B02_耳部感染或手術 = "B02_耳部感染或手術", // D

  B02_如果從事上面的任何工作 = "B02_如果從事上面的任何工作", // E

  單選_連動 = "單選_連動",
}

// 如果有，您知道是什麼原因造成的嗎？
// 請詳細描述_______________________

// □有    □無
// □有    □無
// 上述問題若有，請填寫
// □左耳 □右耳
// □不確定 □雙耳

export function Is連動排除的題目(
  question: UniQuestion,
  rhf: UseFormReturn<FieldValues, any>
) {
  let is_hide = false; // 預設都顯示

  if (question.valid_when_rule !== ValidWhenRule.None) {
    // 有設定連動規則
    if (question.valid_when_rule === ValidWhenRule.單選_連動) {
      let val = rhf.watch(question.valid_when_rhf_name); //ex:m02.1年內長期身體不適
      if (question.valid_when_rhf_value === "undefined") {
        if (val == null || val === "") {
          //pass
        } else {
          is_hide = true;
        }
      } else if (question.valid_when_rhf_value === val) {
        //pass
      } else {
        is_hide = true;
      }
    } else if (
      question.valid_when_rule === ValidWhenRule.B02_如果從事上面的任何工作
    ) {
      let val = rhf.watch("作業經歷.B02_曾經從事.以上皆無");
      if (val === true) {
        is_hide = true; // 不符合條件 --> 隱藏此題
      } else {
        // pass
      }
    } else if (question.valid_when_rule === ValidWhenRule.B02_新進_變更) {
      let val = rhf.watch("檢查時期.B02_檢查時期");
      if (val === "新進" || val === "變更") {
        // pass
      } else {
        is_hide = true; // 不符合條件 --> 隱藏此題
      }

      if (
        is_hide == false &&
        question.question_code ===
          "如果有，您知道是什麼原因造成的嗎？請詳細描述"
      ) {
        let val1 = rhf.watch("自覺症狀(噪音作業).聽力困難現象");
        let val2 = rhf.watch("自覺症狀(噪音作業).耳鳴_1");
        let val3 = rhf.watch("自覺症狀(噪音作業).眩暈");
        is_hide = !(val1 === "有" || val2 === "有" || val3 === "有");
      }
    } else if (question.valid_when_rule === ValidWhenRule.B02_定期_追蹤) {
      let val = rhf.watch("檢查時期.B02_檢查時期");
      if (val === "定期" || val === "追蹤") {
        // pass
      } else {
        is_hide = true; // 不符合條件 --> 隱藏此題
      }
    } else if (question.valid_when_rule === ValidWhenRule.B02_耳部感染或手術) {
      let val1 = rhf.watch(
        "既往病史(噪音作業).動過耳朵手術、耳部外傷或鼓膜穿孔"
      );
      let val2 = rhf.watch("既往病史(噪音作業).耳部感染");

      if ((val1 != null && val1 === "有") || (val2 !== null && val2 === "有")) {
        // pass
      } else {
        is_hide = true; // 不符合條件 --> 隱藏此題
      }
    } else if (question.valid_when_rule === ValidWhenRule.B02_是否配戴護具) {
      let val2 = rhf.watch("作業經歷.B02_個人有戴上聽力防護用具嗎");

      if (
        // (val1 != null && val1 === "有")  ||
        val2 !== null &&
        val2 === "有"
      ) {
        // pass
      } else {
        is_hide = true; // 不符合條件 --> 隱藏此題
      }

      let val = rhf.watch("作業經歷.B02_曾經從事.以上皆無");
      if (val === true) {
        is_hide = true; // 不符合條件 --> 隱藏此題
      } else {
        // pass
      }
    } else if (
      question.valid_when_rule === ValidWhenRule.是否曾經從事其他工作
    ) {
      // let val1 = rhf.watch("作業經歷.B02_現場環境有聽力保護設備嗎");
      let val2 = rhf.watch("作業經歷.是否曾經從事其他工作");

      if (val2 !== null && val2 === "是") {
        // pass
      } else {
        is_hide = true; // 不符合條件 --> 隱藏此題
      }
    } else {
      throw new_ErrorAlert("未知的連動規則!!!" + question.valid_when_rule);
    }
  }
  return is_hide;
}
